import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, TextField, Button, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import InstagramIcon from '../../icons/InstagramIcon';
import DribbbleIcon from '../../icons/DribbbleIcon';
import TwitterIcon from '../../icons/TwitterIcon';
import YouTubeIcon from '../../icons/YouTubeIcon';
import SendIcon from '../../icons/SendIcon';
import logo from '../../../assets/VisibeenLogo.png';

import logger from '../../../utils/logger';
const FooterContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(93.12deg, rgba(11,145,214,1) 0%, rgba(239,35,42,1) 100%)',
  padding: '40px 0',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif'
}));

const FooterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 40px',
  gap: '125px'
}));

const FooterLeft = styled(Stack)(({ theme }) => ({
  gap: '40px',
  alignItems: 'flex-start'
}));

const FooterLogo = styled('img')(({ theme }) => ({
  height: '48px',
  width: 'auto',
  objectFit: 'contain',
}));

const CopyrightSection = styled(Stack)(({ theme }) => ({
  gap: '8px'
}));

const CopyrightText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#ffffff',
  lineHeight: '20px'
}));

const SocialIcons = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '16px'
}));

const SocialIconLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  color: '#ffffff',
  textDecoration: 'none',
  '&:hover': {
    opacity: 0.8
  }
}));

const FooterColumns = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '30px',
  alignItems: 'flex-start'
}));

const FooterColumn = styled(Stack)(({ theme }) => ({
  gap: '24px',
  minWidth: '160px'
}));

const ColumnTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: '#ffffff'
}));

const ColumnLinks = styled(Stack)(({ theme }) => ({
  gap: '12px'
}));

const ColumnLink = styled(Link)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#ffffff',
  textDecoration: 'none',
  '&:hover': {
    opacity: 0.8
  }
}));

const NewsletterContainer = styled(Stack)(({ theme }) => ({
  gap: '24px',
  minWidth: '255px'
}));

const NewsletterForm = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '8px',
  overflow: 'hidden'
}));

const NewsletterInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    height: '40px',
    paddingRight: '40px',
    '& fieldset': {
      border: 'none'
    },
    '& input': {
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      fontWeight: 400,
      color: '#A0A0AA',
      padding: '8px 12px'
    }
  }
}));

const SendButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  right: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  minWidth: '24px',
  width: '24px',
  height: '24px',
  padding: 0,
  color: '#A0A0AA',
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.8
  }
}));

const AdminDashboardFooter = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    logger.log('Email submitted:', email);
    // Add email validation and submission logic here
  };

  const handleQuickTutorial = (e) => {
    e.preventDefault();
    navigate('/client-onboarding');
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterLeft>
          <FooterLogo
            src={logo}
            alt="VISIBEEN"
          />
          <CopyrightSection>
            <CopyrightText>Copyright © 2025 e2e digitech pvt ltd.</CopyrightText>
            <CopyrightText>All rights reserved</CopyrightText>
          </CopyrightSection>
          <SocialIcons>
            <SocialIconLink href="#" aria-label="Instagram">
              <InstagramIcon width={32} height={32} color="#ffffff" />
            </SocialIconLink>
            <SocialIconLink href="#" aria-label="Dribbble">
              <DribbbleIcon width={32} height={32} color="#ffffff" />
            </SocialIconLink>
            <SocialIconLink href="#" aria-label="Twitter">
              <TwitterIcon width={32} height={32} color="#ffffff" />
            </SocialIconLink>
            <SocialIconLink href="#" aria-label="YouTube">
              <YouTubeIcon width={32} height={32} color="#ffffff" />
            </SocialIconLink>
          </SocialIcons>
        </FooterLeft>

        <FooterColumns>
          <FooterColumn>
            <ColumnTitle>Company</ColumnTitle>
            <ColumnLinks>
              <ColumnLink href="#">About us</ColumnLink>
              <ColumnLink href="#">Blog</ColumnLink>
              <ColumnLink href="#" onClick={handleQuickTutorial} style={{ cursor: 'pointer' }}>Quick Tutorial</ColumnLink>
              <ColumnLink href="#">Pricing</ColumnLink>
              <ColumnLink href="#">Testimonials</ColumnLink>
            </ColumnLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Support</ColumnTitle>
            <ColumnLinks>
              <ColumnLink href="#">Help center</ColumnLink>
              <ColumnLink href="#">Terms of service</ColumnLink>
              <ColumnLink href="#">Legal</ColumnLink>
              <ColumnLink href="#">Privacy policy</ColumnLink>
              <ColumnLink href="#">Status</ColumnLink>
            </ColumnLinks>
          </FooterColumn>

          <NewsletterContainer>
            <ColumnTitle>Stay up to date</ColumnTitle>
            <NewsletterForm component="form" onSubmit={handleSubmit}>
              <NewsletterInput
                fullWidth
                placeholder="Your email address"
                value={email}
                onChange={handleEmailChange}
                variant="outlined"
                type="email"
                required
              />
              <SendButton type="submit">
                <SendIcon width={17} height={17} color="#A0A0AA" />
              </SendButton>
            </NewsletterForm>
          </NewsletterContainer>
        </FooterColumns>
      </FooterContent>
    </FooterContainer>
  );
};

export default AdminDashboardFooter;